import { jsx as _jsx } from "react/jsx-runtime";
import { useForkRef } from "@mui/material";
import { DataGridPremium, useGridApiRef, } from "@mui/x-data-grid-premium";
import { deDE, enUS, frFR, nlNL, plPL, ruRU, } from "@mui/x-data-grid-premium/locales/index.js";
import { isEqual, merge } from "lodash-es";
import React from "react";
import { LoadingSpinner } from "../loading/LoadingSpinner.js";
import { useLocale } from "../LocaleProvider.js";
import { useDataGridColumns } from "./useDataGridColumns.js";
export { GridTreeDataGroupingCell } from "@mui/x-data-grid-premium";
const DATAGRID_LOCALES = {
    en: enUS,
    "en-GB": enUS,
    de: deDE,
    "de-DE": deDE,
    "de-RF": deDE,
    fr: frFR,
    "fr-FR": frFR,
    nl: nlNL,
    "nl-NL": nlNL,
    pl: plPL,
    "pl-PL": plPL,
    ru: ruRU,
    "ru-RU": ruRU,
};
const RESULTS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
// could be used for testing or situations where we don't want to use user preferences store
export function useLocalStateStore() {
    const [state, setState] = React.useState(undefined);
    return React.useMemo(() => ({ value: state, saveValue: setState, loading: false }), [state, setState]);
}
export function extendDataGridState(existingState, overrideState) {
    const newState = {
        columns: {
            ...existingState?.columns,
            ...overrideState?.columns,
            dimensions: {
                ...existingState?.columns?.dimensions,
                ...overrideState?.columns?.dimensions,
            },
            pinnedColumns: {
                // @ts-ignore
                ...existingState?.columns?.pinnedColumns,
                // @ts-ignore
                ...overrideState?.columns?.pinnedColumns,
            },
            columnVisibilityModel: {
                ...existingState?.columns?.columnVisibilityModel,
                ...overrideState?.columns?.columnVisibilityModel,
            },
        },
        rowGrouping: {
            ...existingState?.rowGrouping,
            ...overrideState?.rowGrouping,
        },
        aggregation: {
            ...existingState?.aggregation,
            ...overrideState?.aggregation,
            model: {
                ...existingState?.aggregation?.model,
                ...overrideState?.aggregation?.model,
            },
        },
        filter: { ...existingState.filter, ...overrideState?.filter },
        pinnedColumns: {
            ...existingState.pinnedColumns,
            ...overrideState?.pinnedColumns,
        },
        preferencePanel: {
            open: false,
            ...existingState.preferencePanel,
            ...overrideState?.preferencePanel,
        },
    };
    return newState;
}
export const DataGrid = React.memo(({ stateStore, initialState, apiRef, autoHeight = false, density = "compact", onDensityChange, loading, hideFooter, localeText: passedLocaleText, columns, groupingColDef, disableColumnFilter = true, rows, onRowClick, slotProps, 
// SORTING
sortingMode = "server", sortModel, onSortModelChange, 
// PAGINATION
pagination = true, paginationMode, paginationModel, onPaginationModelChange, pageSizeOptions = RESULTS_PER_PAGE_OPTIONS, rowCount, 
// SELECTION
rowSelectionModel, onRowSelectionModelChange, keepNonExistentRowsSelected = true, disableRowSelectionOnClick = true, disableMultipleRowSelection = false, disableAggregation = true, disableRowGrouping = true, 
// OTHER
onResize, onStateChange, onColumnOrderChange, onColumnWidthChange, onColumnVisibilityModelChange, onRowGroupingModelChange, onAggregationModelChange, onFilterModelChange, onPinnedColumnsChange, borderless = false, ...other }) => {
    const locale = useLocale();
    const datagridLocale = DATAGRID_LOCALES[locale] ?? enUS;
    const localeText = React.useMemo(() => {
        return merge({}, datagridLocale.components.MuiDataGrid.defaultProps.localeText, passedLocaleText);
    }, [passedLocaleText, datagridLocale]);
    const gridApiRef = useGridApiRef();
    const localStoreState = useLocalStateStore();
    const stateStoreRef = React.useRef(stateStore ?? localStoreState);
    stateStoreRef.current = stateStore ?? localStoreState;
    const state = (stateStore ?? localStoreState).value ?? initialState;
    const saveStateSnapshot = React.useCallback((overrideState) => {
        const newState = extendDataGridState(stateStoreRef.current?.value ?? {}, overrideState ?? {});
        if (stateStoreRef.current &&
            (!stateStoreRef.current.value ||
                !isEqual(stateStoreRef.current.value, newState))) {
            stateStoreRef.current.saveValue(newState);
        }
    }, []);
    const { columnsExtended, groupingColDefExtended } = useDataGridColumns(state, columns, groupingColDef);
    const sortModelFiltered = React.useMemo(() => {
        return sortModel?.filter(model => 
        // filtering out sort models which are not in columns definition
        columns.some(col => col.field === model.field));
    }, [sortModel, columns]);
    const ref = useForkRef(apiRef, gridApiRef);
    // we don't want to render any table unless state is loaded – to prevent having callbacks with wrong state
    if ((stateStore ?? localStoreState).loading)
        return _jsx(LoadingSpinner, {});
    return (_jsx(DataGridPremium, { 
        // @ts-ignore
        apiRef: ref, autoHeight: autoHeight, density: state?.density ?? density, loading: loading, hideFooter: hideFooter, localeText: localeText, columns: columnsExtended, groupingColDef: groupingColDefExtended, disableColumnFilter: disableColumnFilter, rows: rows, onRowClick: onRowClick, 
        // SORTING
        sortingMode: sortingMode, sortModel: sortModelFiltered, onSortModelChange: onSortModelChange, 
        // PAGINATION
        pagination: pagination, paginationMode: pagination ? (paginationMode ?? "server") : undefined, paginationModel: paginationModel, onPaginationModelChange: onPaginationModelChange, pageSizeOptions: pageSizeOptions, rowCount: rowCount, 
        // SELECTION
        rowSelectionModel: rowSelectionModel, onRowSelectionModelChange: onRowSelectionModelChange, keepNonExistentRowsSelected: keepNonExistentRowsSelected, disableRowSelectionOnClick: disableRowSelectionOnClick, disableMultipleRowSelection: disableMultipleRowSelection, disableAggregation: disableAggregation, disableRowGrouping: disableRowGrouping, initialState: state, columnVisibilityModel: state?.columns?.columnVisibilityModel, rowGroupingModel: state?.rowGrouping?.model, aggregationModel: state?.aggregation?.model, filterModel: state?.filter?.filterModel, slotProps: {
            ...slotProps,
            columnsManagement: {
                disableShowHideToggle: true,
                ...slotProps?.columnsManagement,
            },
        }, 
        // @ts-ignore
        pinnedColumns: state?.columns?.pinnedColumns ?? {}, onPinnedColumnsChange: (...args) => {
            saveStateSnapshot({
                // @ts-ignore
                columns: { pinnedColumns: args[0] },
            });
            return onPinnedColumnsChange?.(...args);
        }, onColumnOrderChange: (...args) => {
            const state = gridApiRef.current.exportState();
            if (state?.columns?.orderedFields) {
                saveStateSnapshot({
                    columns: { orderedFields: state?.columns?.orderedFields },
                });
            }
            return onColumnOrderChange?.(...args);
        }, onColumnWidthChange: (...args) => {
            if (!args[0].colDef.width)
                return;
            saveStateSnapshot({
                columns: {
                    dimensions: {
                        [args[0].colDef.field]: {
                            width: Math.round(args[0].colDef.width),
                        },
                    },
                },
            });
            return onColumnWidthChange?.(...args);
        }, onColumnVisibilityModelChange: (...args) => {
            saveStateSnapshot({ columns: { columnVisibilityModel: args[0] } });
            return onColumnVisibilityModelChange?.(...args);
        }, onRowGroupingModelChange: (...args) => {
            saveStateSnapshot({ rowGrouping: { model: args[0] } });
            return onRowGroupingModelChange?.(...args);
        }, onAggregationModelChange: (...args) => {
            saveStateSnapshot({ aggregation: { model: args[0] } });
            return onAggregationModelChange?.(...args);
        }, onFilterModelChange: (...args) => {
            saveStateSnapshot({ filter: { filterModel: args[0] } });
            return onFilterModelChange?.(...args);
        }, onDensityChange: (...args) => {
            saveStateSnapshot({ density: args[0] });
            return onDensityChange?.(...args);
        }, onStateChange: onStateChange, onResize: onResize, ...(borderless
            ? {
                sx: {
                    "&": { border: "none" },
                    "& .MuiDataGrid-columnHeaders": {
                        borderBottom: "none",
                    },
                    "& .MuiDataGrid-cell": {
                        padding: "0 4px",
                        borderBottom: "none",
                    },
                    "& .MuiDataGrid-columnHeader": {
                        padding: "0 6px",
                    },
                },
            }
            : undefined), ...other }));
});
//# sourceMappingURL=DataGrid.js.map